// What the E-Mails screen knows about the log's codes, in one place. The backend sends the code
// (MailType, MailGroup, trigger, status); every label is `emailAdmin.<kind>.<CODE>` in de.json and
// en.json. An unknown code still renders as words (the underscored code in sentence case) instead of
// a raw key, so a MailType shipped after a release is readable before its translation is.
import { eventNameFor } from '../../utils/eventName';

// The groups in the order the type dropdown lists them (backend MailGroup).
export const GROUP_ORDER = ['INVITATIONS', 'PROGRAM', 'EVENT', 'MATERIALS', 'ACCOUNT', 'REGISTRATION', 'SUBMISSIONS', 'CREW'];

// The trigger and status choices the log filters offer; the values are the backend's codes.
export const TRIGGERS = ['AUTOMATIC', 'ADMIN', 'SYSTEM'];
export const STATUSES = ['SENT', 'FAILED'];

// One page of the log: the backend's default, and the most it serves.
export const SIZE = 50;
export const MAX_SIZE = 200;

const readable = (code) => String(code)
  .toLowerCase()
  .replace(/_/g, ' ')
  .replace(/^./, (first) => first.toUpperCase());

export const typeLabel = (t, type) => (type ? t(`emailAdmin.type.${type}`, readable(type)) : '—');

export const groupLabel = (t, group) => (group ? t(`emailAdmin.group.${group}`, readable(group)) : '—');

export const statusLabel = (t, status) => (status ? t(`emailAdmin.status.${status}`, readable(status)) : '—');

// Who a template is addressed to (backend Recipient).
export const recipientLabel = (t, recipient) => (recipient
  ? t(`emailAdmin.recipient.${recipient}`, readable(recipient)) : '—');

// A template's name and its "when it is sent" sentence. An ordinary template translates per key; a
// schedule's template (the backend sends `schedule`, P4a) reads the type, the step's audience and its
// days from the API, so "one week" and "two days" are never fixed text. `template` is the API object
// (the list row or the opened template); a bare key is still accepted for older callers.
const scheduleOf = (template) => (typeof template === 'string' ? null : template?.schedule || null);

const stepName = (t, schedule) => t('emailAdmin.schedule.step.name',
  '{audience} – {days, plural, one {# day} other {# days}} before', {
    audience: t(`emailAdmin.schedule.step.audience.${schedule.audience}`, readable(schedule.audience)),
    days: schedule.daysBeforeStart,
  });

export const templateName = (t, template) => {
  if (!template) return '—';
  const key = typeof template === 'string' ? template : template.key;
  const schedule = scheduleOf(template);
  if (schedule?.daysBeforeStart != null) return stepName(t, schedule);
  return t(`emailAdmin.template.${key}.name`, readable(key));
};

const OFF = (t, schedule) => (schedule?.enabled === false
  ? `${t('emailAdmin.schedule.off', 'Automatic sending is off for this type.')} ` : '');

export const templateWhen = (t, template) => {
  if (!template) return '';
  const key = typeof template === 'string' ? template : template.key;
  const schedule = scheduleOf(template);
  if (schedule?.shape === 'REPEATING') {
    return OFF(t, schedule) + t('emailAdmin.schedule.invite.when',
      'Automatically at {time} (Vienna) for an invitation that is still unanswered: {days, plural, one {# day} other {# days}} after the invitation or the last reminder, at most {max, plural, one {# time} other {# times}}, until the person answers.',
      { time: schedule.sendTime, days: schedule.intervalDays, max: schedule.maxCount });
  }
  if (schedule?.daysBeforeStart != null) {
    return OFF(t, schedule) + t('emailAdmin.schedule.step.when',
      'Automatically at {time} (Vienna), {days, plural, one {# day} other {# days}} before the event starts, to {recipients}.',
      {
        time: schedule.sendTime,
        days: schedule.daysBeforeStart,
        recipients: t(`emailAdmin.schedule.step.recipients.${schedule.audience}`, 'the recipients'),
      });
  }
  return t(`emailAdmin.template.${key}.when`, '');
};

// The placeholder groups the backend sends are English prose; the editor translates the nine it
// knows and shows any later one as it arrived.
const PLACEHOLDER_GROUPS = {
  Recipient: 'recipient',
  Event: 'event',
  Invitation: 'invitation',
  'Ready-made blocks': 'blocks',
  Account: 'account',
  'Crew notice': 'crew',
  Registration: 'registration',
  Submission: 'submission',
  Program: 'program',
};

export const placeholderGroupLabel = (t, group) => (PLACEHOLDER_GROUPS[group]
  ? t(`emailAdmin.phGroup.${PLACEHOLDER_GROUPS[group]}`, group) : group);

// A refused save's problems, verbatim; null when the answer is not that shape.
export const saveProblems = (err) => {
  const problems = err?.response?.data?.problems;
  return Array.isArray(problems) ? problems : null;
};

// "Automatic" / "Admin or team: Admin Name" / "System": who caused the email. Only an ADMIN email has
// a sender (sent_by_account_id, V58).
export const triggerLabel = (t, trigger, sentByName) => {
  if (!trigger) return '—';
  const label = t(`emailAdmin.trigger.${trigger}`, readable(trigger));
  return trigger === 'ADMIN' && sentByName
    ? t('emailAdmin.trigger.by', '{trigger}: {name}', { trigger: label, name: sentByName })
    : label;
};

// The event name in the site language, for the filter and the rows.
export const eventTitle = (locale, event) => eventNameFor(locale, event?.nameDe, event?.nameEn) || (event ? `#${event.id}` : '');

// The log's time in Vienna, as the owner reads dates: dd.MM.yyyy HH:mm. Assembled from the parts of
// an explicit Europe/Vienna format (the browser's own zone must not leak into an admin table).
const VIENNA_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Vienna',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

export const viennaTime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const parts = Object.fromEntries(VIENNA_TIME.formatToParts(date).map((part) => [part.type, part.value]));
  return `${parts.day}.${parts.month}.${parts.year} ${parts.hour}:${parts.minute}`;
};

// The query the table sends: the empty filters are left out, so the URL carries only what was chosen.
// `q` is trimmed; eventId is a number; the dates stay YYYY-MM-DD (Vienna, inclusive).
export const logParams = (filters = {}, { page = 0, size = SIZE } = {}) => {
  const params = { page, size: Math.min(size, MAX_SIZE) };
  const put = (key, value) => {
    if (value === '' || value === null || value === undefined) return;
    params[key] = value;
  };
  put('type', filters.type);
  put('eventId', filters.eventId ? Number(filters.eventId) : null);
  put('q', (filters.q || '').trim());
  put('status', filters.status);
  put('trigger', filters.trigger);
  put('from', filters.from);
  put('to', filters.to);
  return params;
};

// The type dropdown's optgroups: the backend's groups in GROUP_ORDER, a type it adds later in its own
// group at the end.
const grouped = (items = []) => {
  const byGroup = new Map();
  for (const item of items) {
    if (!byGroup.has(item.group)) byGroup.set(item.group, []);
    byGroup.get(item.group).push(item);
  }
  const order = [...GROUP_ORDER.filter((group) => byGroup.has(group)), ...byGroup.keys()].filter(
    (group, index, all) => all.indexOf(group) === index,
  );
  return order.map((group) => ({ group, items: byGroup.get(group) }));
};

export const typeGroups = (types = []) => grouped(types).map(({ group, items }) => ({ group, types: items }));

// The catalogue's groups in the same order, in the order the backend lists each group's templates.
export const templateGroups = (templates = []) => grouped(templates);

// ------------------------------------------------------------------ the automatic schedules (P4b)

// The order of the four automatic types on the Schedules tab (and the backend's own order).
export const SCHEDULE_KINDS = ['INVITE_REMINDER', 'EVENT_REMINDER', 'MATERIALS_REMINDER', 'SPEAKER_SHARE_REMINDER'];

export const scheduleName = (t, kind) => t(`emailAdmin.schedules.name.${kind}`, readable(kind));

// "7 days" - the unit of a countdown step, translated.
export const scheduleDayText = (t, days) => t('emailAdmin.schedules.dayCount',
  '{days, plural, one {# day} other {# days}}', { days });

// A countdown's days newest first (60 … 1), the order the sentence and the list read.
export const scheduleSteps = (schedule) => [...(schedule?.steps || [])]
  .sort((a, b) => b.daysBeforeStart - a.daysBeforeStart);

// What the schedule does now, in the site language, from the API's fields only: the backend's own
// `summary` is English, so the screen never shows it. An off schedule says so first.
export const scheduleSentence = (intl, schedule) => {
  if (!schedule) return '';
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const off = schedule.enabled === false
    ? `${t('emailAdmin.schedules.sentence.off', 'Off – no automatic emails of this type.')} ` : '';
  if (schedule.shape === 'REPEATING') {
    return off + t('emailAdmin.schedules.sentence.repeating',
      'Every {days, plural, one {# day} other {# days}}, at most {max, plural, one {# automatic reminder} other {# automatic reminders}}, at {time} Vienna time.',
      { days: schedule.intervalDays, max: schedule.maxCount, time: schedule.sendTime });
  }
  const days = intl.formatList(scheduleSteps(schedule).map((step) => scheduleDayText(t, step.daysBeforeStart)),
    { type: 'conjunction' });
  return off + (days
    ? t('emailAdmin.schedules.sentence.countdown', '{days} before the event starts, at {time} Vienna time.',
      { days, time: schedule.sendTime })
    : t('emailAdmin.schedules.sentence.noDays', 'At {time} Vienna time.', { time: schedule.sendTime }));
};

// What went wrong, as the backend says it: the admin API answers a plain-text body, so that text is
// the message the screen shows.
export const errorText = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data) return data;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  if (err?.message) return err.message;
  return fallback;
};

// ------------------------------------------------------------------ the Upcoming tab (P5)

// The three automatic types in a row's own words (the Schedules headings are plural).
export const upcomingTypeLabel = (t, kind) => t(`emailAdmin.upcoming.type.${kind}`, readable(kind));

// "Event reminders · 7 days before" - the type and, for a countdown, the step's day.
export const upcomingWhen = (intl, row) => {
  const type = intl.formatMessage({ id: `emailAdmin.upcoming.type.${row.kind}`, defaultMessage: readable(row.kind) });
  if (row.daysBeforeStart == null) return type;
  return `${type} · ${intl.formatMessage(
    { id: 'emailAdmin.upcoming.daysBefore', defaultMessage: '{days, plural, one {# day} other {# days}} before' },
    { days: row.daysBeforeStart },
  )}`;
};

// The audience of a countdown row; a repeating invite chain has none.
export const upcomingAudience = (t, row) => (row.audience
  ? t(`emailAdmin.schedule.step.recipients.${row.audience}`, row.audience) : null);

export const upcomingCount = (intl, count) => intl.formatMessage(
  { id: 'emailAdmin.upcoming.count', defaultMessage: '{count, plural, one {# person} other {# people}}' },
  { count },
);

// Today and tomorrow are relative to the API's own `from` (Vienna today), never the browser's clock.
// Every other day is its weekday and date, formatted in UTC so the Vienna date never shifts.
const shiftDate = (iso, days) => {
  const date = new Date(`${iso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export const upcomingDateLabel = (intl, date, from) => {
  if (date === from) return intl.formatMessage({ id: 'emailAdmin.upcoming.today', defaultMessage: 'Today' });
  if (date === shiftDate(from, 1)) {
    return intl.formatMessage({ id: 'emailAdmin.upcoming.tomorrow', defaultMessage: 'Tomorrow' });
  }
  return intl.formatDate(new Date(`${date}T12:00:00Z`), {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
};
